import * as React from "react";

export type NativeSheetRequestClose = () => void;

/** Return true if an exit animation was started (caller must not close yet). */
export type NativeSheetDismissAnimator = (
  onComplete: () => void,
  fromDy?: number,
) => boolean;

type NativeSheetDismissContextValue = {
  requestClose: NativeSheetRequestClose;
  registerDismissAnimator: (animator: NativeSheetDismissAnimator | null) => void;
};

const NativeSheetDismissContext =
  React.createContext<NativeSheetDismissContextValue | null>(null);

export function useNativeSheetDismissContext() {
  return React.useContext(NativeSheetDismissContext);
}

export function NativeSheetDismissProvider({
  onRequestClose,
  beforeDismiss,
  children,
}: {
  onRequestClose: NativeSheetRequestClose;
  /** Return false to veto dismiss (no animation, sheet stays open). */
  beforeDismiss?: () => boolean;
  children: React.ReactNode;
}) {
  const animatorRef = React.useRef<NativeSheetDismissAnimator | null>(null);

  const registerDismissAnimator = React.useCallback(
    (animator: NativeSheetDismissAnimator | null) => {
      animatorRef.current = animator;
    },
    [],
  );

  const requestClose = React.useCallback(() => {
    if (beforeDismiss?.() === false) {
      return;
    }
    const animator = animatorRef.current;
    if (animator?.(onRequestClose)) {
      return;
    }
    onRequestClose();
  }, [onRequestClose, beforeDismiss]);

  const value = React.useMemo(
    () => ({ requestClose, registerDismissAnimator }),
    [requestClose, registerDismissAnimator],
  );

  return (
    <NativeSheetDismissContext.Provider value={value}>
      {children}
    </NativeSheetDismissContext.Provider>
  );
}
